import React, {useContext, useEffect, useState, useCallback} from "react";
import {useParams, Link} from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {oneDark, oneLight} from "react-syntax-highlighter/dist/esm/styles/prism";
import SyntaxHighlighter from "../../utils/registerApex";
import StyleContext from "../../contexts/StyleContext";
import {useAuth} from "../../contexts/AuthContext";
import {
  fetchBlog,
  incrementView,
  likeBlog,
  postComment
} from "../../services/api";
import {timeAgo} from "../../utils/timeAgo";
import AuthModal from "../../components/auth/AuthModal";
import GlobalLoader from "../../components/loader/GlobalLoader";
import "./Blog.scss";

function embedFromUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.replace(/\//g, "");
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    return null;
  } catch {
    return null;
  }
}

// ----- Recursive comment thread -----
const CommentNode = ({node, depth, onReply, isAdmin, replyingTo, setReplyingTo, onSubmitReply, replyText, setReplyText, busy}) => {
  const isReplying = replyingTo === node.id;
  return (
    <div className={`blog-comment depth-${Math.min(depth, 3)}`}>
      <div className="blog-comment__head">
        <strong>{node.user_name || "User"}</strong>
        {node.is_admin && <span className="blog-comment__badge">Admin</span>}
        <span className="blog-comment__time">· {timeAgo(node.created_at)}</span>
      </div>
      <div className="blog-comment__body">{node.comment_text}</div>
      {isAdmin && (
        <div className="blog-comment__actions">
          <button
            type="button"
            className="blog-comment__reply-btn"
            onClick={() => setReplyingTo(isReplying ? null : node.id)}
          >
            {isReplying ? "Cancel" : "Reply"}
          </button>
        </div>
      )}
      {isReplying && (
        <form
          className="blog-comment__reply-form"
          onSubmit={e => { e.preventDefault(); onSubmitReply(node.id); }}
        >
          <textarea
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Write your reply…"
            autoFocus
          />
          <div className="blog-comment__reply-actions">
            <button type="submit" disabled={busy || !replyText.trim()}>
              {busy ? "Posting…" : "Post reply"}
            </button>
          </div>
        </form>
      )}
      {node.replies && node.replies.length > 0 && (
        <div className="blog-comment__replies">
          {node.replies.map(child => (
            <CommentNode
              key={child.id}
              node={child}
              depth={depth + 1}
              isAdmin={isAdmin}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              onSubmitReply={onSubmitReply}
              replyText={replyText}
              setReplyText={setReplyText}
              busy={busy}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BlogDetail = () => {
  const {id} = useParams();
  const {isDark} = useContext(StyleContext);
  const {token, isAuthenticated, isAdmin} = useAuth();
  const [blog, setBlog] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [liked, setLiked] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchBlog(id);
      setBlog(data.blog || null);
      setComments(data.comments || []);
      setOffline(!!data.offline);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Smart view tracking — fires once per session, server enforces 24h dedup.
  useEffect(() => {
    if (!blog) return;
    const key = `viewed_${blog.id}`;
    if (!sessionStorage.getItem(key)) {
      incrementView(blog.id, token).then(res => {
        if (res?.counted && res.views) {
          setBlog(b => (b ? {...b, views: res.views} : b));
        }
      });
      sessionStorage.setItem(key, "1");
    }
  }, [blog, token]);

  const handleLike = async () => {
    if (!blog || liked) return;
    setLiked(true);
    setBlog(b => ({...b, likes: (b.likes || 0) + 1}));
    await likeBlog(blog.id, token);
  };

  const handleCommentFocus = () => {
    if (!isAuthenticated) setShowAuth(true);
  };

  const handleSubmitComment = async e => {
    e.preventDefault();
    if (!isAuthenticated) { setShowAuth(true); return; }
    const text = commentText.trim();
    if (!text) return;
    setPosting(true);
    try {
      const res = await postComment(blog.id, text, token);
      const newComment = res.comment;
      // Top-level: prepend to thread.
      setComments(c => [newComment, ...c]);
      setCommentText("");
    } catch (e2) {
      setError(e2.message);
    } finally {
      setPosting(false);
    }
  };

  // Insert a reply into the tree by walking it.
  const insertReply = (nodes, parentId, reply) => {
    return nodes.map(n => {
      if (n.id === parentId) {
        return {...n, replies: [...(n.replies || []), reply]};
      }
      if (n.replies?.length) {
        return {...n, replies: insertReply(n.replies, parentId, reply)};
      }
      return n;
    });
  };

  const handleSubmitReply = async parentId => {
    const text = replyText.trim();
    if (!text) return;
    setPosting(true);
    try {
      const res = await postComment(blog.id, text, token, parentId);
      setComments(c => insertReply(c, parentId, res.comment));
      setReplyText("");
      setReplyingTo(null);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <main className={`blog-detail ${isDark ? "dark" : ""}`}>
        <GlobalLoader inline label="Loading post" />
      </main>
    );
  }
  if (error || !blog) {
    return (
      <main className={`blog-detail ${isDark ? "dark" : ""}`}>
        <Link to="/" className="blog-detail__back">← Back to all posts</Link>
        <h1 className="blog-detail__title">Post unavailable</h1>
        <p>{error || "We couldn't find this post."}</p>
      </main>
    );
  }

  const flatCount = (function count(nodes) {
    return nodes.reduce((n, c) => n + 1 + count(c.replies || []), 0);
  })(comments);

  return (
    <main className={`blog-detail ${isDark ? "dark" : ""}`}>
      <Link to="/" className="blog-detail__back">← Back to all posts</Link>
      <h1 className="blog-detail__title">{blog.title}</h1>
      <div className="blog-detail__meta">
        <span>{blog.author || "Rashid Ahmad"}</span>
        <span>•</span>
        <span>Posted {timeAgo(blog.created_at)}</span>
        <span>•</span>
        <span>{blog.views || 0} views</span>
        {offline && <span style={{color: "#b35900"}}>• (offline copy)</span>}
      </div>
      {blog.cover_image && (
        <img className="blog-detail__cover" src={blog.cover_image} alt="" />
      )}

      <article className="blog-detail__body">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({inline, className, children, ...props}) {
              const match = /language-(\w+)/.exec(className || "");
              const lang = (match ? match[1] : "").toLowerCase();
              if (inline) {
                return <code className={className} {...props}>{children}</code>;
              }
              return (
                <SyntaxHighlighter
                  style={isDark ? oneDark : oneLight}
                  language={lang || "javascript"}
                  PreTag="div"
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              );
            },
            a({href, children}) {
              const embed = href && embedFromUrl(href);
              if (embed) {
                return (
                  <div className="blog-detail__embed">
                    <iframe
                      src={embed}
                      title="Embedded video"
                      allowFullScreen
                      allow="accelerometer; encrypted-media; picture-in-picture"
                    />
                  </div>
                );
              }
              return (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              );
            }
          }}
        >
          {blog.content || ""}
        </ReactMarkdown>
      </article>

      <div className="blog-actions">
        <button
          className={`blog-actions__btn ${liked ? "liked" : ""}`}
          onClick={handleLike}
          disabled={liked}
        >
          ♥ Like · {blog.likes || 0}
        </button>
        <span style={{color: "#888"}}>{flatCount} comments</span>
      </div>

      <section className="blog-comments">
        <h3 className="blog-comments__title">Comments</h3>
        <form onSubmit={handleSubmitComment}>
          <textarea
            className="blog-comments__box"
            placeholder={isAuthenticated ? "Share your thoughts…" : "Login to leave a comment…"}
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            onFocus={handleCommentFocus}
            readOnly={!isAuthenticated}
          />
          {isAuthenticated && (
            <button
              type="submit"
              className="blog-comments__submit"
              disabled={posting || !commentText.trim()}
            >
              {posting ? "Posting…" : "Post comment"}
            </button>
          )}
        </form>

        {comments.length === 0 ? (
          <p style={{color: "#888", marginTop: 18}}>Be the first to comment.</p>
        ) : (
          <div className="blog-comments__list">
            {comments.map(c => (
              <CommentNode
                key={c.id}
                node={c}
                depth={0}
                isAdmin={isAdmin}
                replyingTo={replyingTo}
                setReplyingTo={setReplyingTo}
                onSubmitReply={handleSubmitReply}
                replyText={replyText}
                setReplyText={setReplyText}
                busy={posting}
              />
            ))}
          </div>
        )}
      </section>

      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      )}
    </main>
  );
};

export default BlogDetail;
